import moment from 'moment-timezone';
import { ISessionTimelineSummaryData, ISessionTimelineTableDataItem } from '@src/api/appData';
import { INTERNAL_UNKNOWN, UNKNOWN } from '@src/utils/constants';
import humanizeValue from '@utils/humanizeUtil';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import type { SessionTimelineTableItem, UserTimelineEvent } from '@conviva/organism-components';

const EmptyArray: any[] = [];

export const formatDate = (timestamp: string): string => {
  if (moment(timestamp).isValid()) {
    return moment(timestamp).format('MMM DD YYYY');
  }
  return '';
};

export const formatTime = (timestamp: string): string => {
  if (moment(timestamp).isValid()) {
    return moment(timestamp).format('HH:mm:ss');
  }
  return '';
};

export const formatTimeDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  let formattedTime = '';

  if (hours > 0) {
    formattedTime += `${hours}h `;
  }
  if (minutes > 0) {
    formattedTime += `${minutes}m `;
  }
  formattedTime += `${remainingSeconds}s`;

  return formattedTime;
};

export const processSummaryData = (summaryData: ISessionTimelineSummaryData) => {
  const { userInfoData, eventSummaryData } = summaryData ?? {};
  const res: any[] = [];
  if (userInfoData && userInfoData.length > 0) {
    res.push({
      label: 'User Info',
      items: userInfoData.map((item) => ({
        value: item.value,
        label: item.name,
      })),
    });
  }
  if (eventSummaryData && eventSummaryData.length > 0) {
    res.push({
      label: 'Event Summary',
      items: eventSummaryData.map((item) => ({
        value: item.value,
        label: item.name,
      })),
      showTooltip: false,
    });
  }
  if (res.length === 0) {
    return EmptyArray;
  }
  return res;
};

export const getFieldType = (
  field: string,
  options: Record<string, Record<string, string | boolean>[]>,
) => {
  for (const category in options) {
    if (Object.prototype.hasOwnProperty.call(options, category)) {
      const item = options[category].find((option) => option.key === field);
      if (item) {
        return item.type;
      }
    }
  }
  return null;
};

export const processSessionTableData = (
  data: ISessionTimelineTableDataItem[],
  impactedSession: string[],
  fields: string[],
  options: Record<string, Record<string, string | boolean>[]>,
) => {
  const res: SessionTimelineTableItem[] = [];
  let index = 1;
  data.forEach((item) => {
    const startDate = formatDate(item.eventTimeStampStart);
    const isOutOfSession = item.inSession !== 1;
    const eventTimeStamp = `${startDate}, ${formatTime(item.eventTimeStampStart)} - ${formatTime(item.eventTimeStampEnd)}`;
    const userActiveTime = isOutOfSession ? 'NA' : formatTimeDuration(item.userActiveTime);
    const sessionIndex = isOutOfSession ? '*' : index++;

    const newItem = {};
    fields.forEach((field) => {
      const type = getFieldType(field, options);
      if (item[field] === 'CONVIVA_INTERNAL_UNKNOWN') {
        newItem[field] = 'UNKNOWN';
        return;
      }
      if (type === 'metrics') {
        const { humanizedNumber, humanizedUnit } = humanizeValue(item[field], field);
        if (!humanizedNumber) {
          newItem[field] = 'NA';
          return;
        }
        newItem[field] = `${humanizedNumber} ${humanizedUnit}`;
        return;
      }
      newItem[field] = item[field];
    });

    res.push({
      ...item,
      ...newItem,
      userActiveTime,
      eventTimeStamp,
      index: sessionIndex,
      impacted: impactedSession.includes(item.sessionId) ? 'Impacted' : '',
    });
  });

  return res;
};

export const matchAttrWithFilter = (attr: string, filter: string, op: string) => {
  if (!attr || typeof attr !== 'string' || !filter) {
    return false;
  }
  let res = false;
  const resAttr = attr;
  switch (op) {
    case 'eq':
      res = resAttr === filter;
      break;
    case 'not_eq':
      res = resAttr !== filter;
      break;
    case 'contains':
      res = resAttr.includes(filter);
      break;
    case 'not_contains':
      res = !resAttr.includes(filter);
      break;
    default:
      break;
  }
  return res;
};

const getValidGlobalFitlers = (data) => {
  const res = data.map((group) => group.filter((item) => item.value || item.display));
  const allEmpty = res.every((group) => group.length === 0);
  return allEmpty ? [] : res;
};

export const processEventAttributeWithGF = (
  eventAttributes: Record<string, string | number>,
  globalFilters: any[],
  dimensionsList: any[],
  eventName: string,
  eventNames: string[],
) => {
  const newEventAttr = {};
  const impactedAttributes: string[] = [];

  // replace field name with display name
  for (const [key, value] of Object.entries(eventAttributes)) {
    if (key.startsWith('event.')) {
      const field = key.slice('event.'.length);
      const displayName = (dimensionsList as any).find((d) => d.field === field)?.name ?? field;
      const resValue = value === INTERNAL_UNKNOWN ? UNKNOWN : value;
      newEventAttr[displayName] = resValue;
      // add extra user id field for accounts with user email
      // so that they can show user id line in event list
      if (displayName === 'User Email') {
        newEventAttr['User ID'] = resValue;
      }
      continue;
    }
    newEventAttr[key] = value;
  }

  let impacted = false;
  const validGlobalFilters = getValidGlobalFitlers(globalFilters);
  if (validGlobalFilters.length === 0) {
    impacted = eventNames.includes(eventName);
  } else {
    let counter = 0;
    validGlobalFilters.forEach((group) => {
      // check if this group already has matched attribute
      let flag = false;
      group.forEach((filter) => {
        const { fieldName, display, op } = filter;
        if (matchAttrWithFilter(newEventAttr[fieldName], display, op)) {
          if (!flag) {
            counter++;
            flag = true;
          }
          impactedAttributes.push(fieldName);
        }
      });
    });

    impacted = counter > 0 && counter === validGlobalFilters.length;

    if (eventNames.length > 0) {
      impacted = impacted && eventNames.includes(eventName);
    }
  }

  return {
    eventAttributes: newEventAttr,
    impactedAttributes,
    impacted,
  };
};

export const getFlowTimelineEventLabel = (
  eventName: string,
  flowMetadata?: AppFlowMetadata,
): UserTimelineEvent['flowEvent'] => {
  const flowStartEvent = flowMetadata?.definition.events.start_event?.name;
  const flowEndEvent = flowMetadata?.definition.events.success_event?.name;
  const flowErrorEvents =
    flowMetadata?.definition.events.error_event.map((item) => item.name) ?? [];

  if (eventName === flowStartEvent) {
    return {
      type: 'start',
      label: 'Initial Event',
    };
  }

  if (eventName === flowEndEvent) {
    return {
      type: 'success',
      label: 'Complete Event',
    };
  }

  if (flowErrorEvents.includes(eventName)) {
    return {
      type: 'error',
      label: 'Error Event',
    };
  }

  return undefined;
};

export const getFlowTimelineEventLabelNew = (
  event: UserTimelineEvent,
  sessionFlows: Record<string, any>[],
): UserTimelineEvent['flowEvent'] => {
  if (!sessionFlows || sessionFlows.length === 0) {
    return undefined;
  }

  const normalizeTimestamp = (timestamp: string): string => {
    return moment(timestamp).format('YYYY-MM-DDTHH:mm:ss');
  };

  const normalizedEventTimestamp = normalizeTimestamp(event.derivedTstamp);

  for (const flow of sessionFlows) {
    const {
      flow_start_event,
      flow_end_event,
      flow_start_timestamp,
      flow_end_timestamp,
      flow_name,
    } = flow;

    if (
      event.eventName === flow_start_event &&
      normalizedEventTimestamp === normalizeTimestamp(flow_start_timestamp)
    ) {
      return {
        type: 'start',
        label: `${flow_name} Initial Event`,
      };
    }

    if (
      event.eventName === flow_end_event &&
      normalizedEventTimestamp === normalizeTimestamp(flow_end_timestamp)
    ) {
      return {
        type: 'success',
        label: `${flow_name} Complete Event`,
      };
    }
  }

  return undefined;
};

export function formatFlowIndexData(
  flowIndexData: Record<string, string | number>[],
  impactedFlowIndexData: Record<string, string | number>[],
  fields: string[],
  fieldsTypeMapping: Map<string, 'metrics' | 'dimensions'>,
  flowFieldsMapping: {
    startTimestamp: string;
    endTimestamp: string;
    duration: string;
    success: string;
    complete: string;
    criticalErrorFlow?: string;
    nonCriticalErrorFlow?: string;
  },
): SessionTimelineTableItem[] {
  const toMs = (s?: string) => {
    const m = moment.utc(s, moment.ISO_8601, true);
    return m.isValid() ? m.valueOf() : Number.POSITIVE_INFINITY;
  };

  // Sort by flow_start_timestamp and flow_end_timestamp
  const sortedFlowIndexData = flowIndexData.toSorted((a, b) => {
    const sa = toMs(a[flowFieldsMapping.startTimestamp] as string);
    const sb = toMs(b[flowFieldsMapping.startTimestamp] as string);

    if (sa !== sb) return sa - sb;

    const ea = toMs(a[flowFieldsMapping.endTimestamp] as string);
    const eb = toMs(b[flowFieldsMapping.endTimestamp] as string);

    return ea - eb;
  });

  const impactedFlowIndexDataSet = new Set(
    impactedFlowIndexData.map(
      (item) => `${item[flowFieldsMapping.startTimestamp]}@${item[flowFieldsMapping.endTimestamp]}`,
    ),
  );

  return sortedFlowIndexData.map((item, index) => {
    const startTimestamp = item[flowFieldsMapping.startTimestamp] as string;
    const endTimestamp = item[flowFieldsMapping.endTimestamp] as string;
    const id = `${startTimestamp}@${endTimestamp}`;
    const duration = item[flowFieldsMapping.duration] as number;
    const complete = item[flowFieldsMapping.complete] as number;
    const success = item[flowFieldsMapping.success] as number;
    const flowStatus = ['Abandoned', 'Completed', 'Success'][complete + success] ?? 'Success';
    const flowErrorStatus =
      !!flowFieldsMapping.criticalErrorFlow &&
      !!flowFieldsMapping.nonCriticalErrorFlow &&
      (typeof item[flowFieldsMapping.criticalErrorFlow] === 'number'
        ? (item[flowFieldsMapping.criticalErrorFlow] as number)
        : 0) +
        (typeof item[flowFieldsMapping.nonCriticalErrorFlow] === 'number'
          ? (item[flowFieldsMapping.nonCriticalErrorFlow] as number)
          : 0) >
        0;

    const formattedItem = fields.reduce((acc, field) => {
      if (item[field] === 'CONVIVA_INTERNAL_UNKNOWN') {
        acc[field] = 'UNKNOWN';
        return acc;
      }

      if (fieldsTypeMapping.get(field) === 'metrics') {
        const { humanizedNumber, humanizedUnit } = humanizeValue(item[field] as number, field);

        if (!humanizedNumber) {
          acc[field] = 'NA';
          return acc;
        }

        acc[field] = `${humanizedNumber} ${humanizedUnit}`;

        return acc;
      }

      acc[field] = item[field];

      return acc;
    }, {});

    return {
      ...item,
      ...formattedItem,
      index: index + 1,
      sessionId: id,
      eventTimeStampStart: startTimestamp,
      eventTimeStampEnd: endTimestamp,
      eventTimeStamp: `${formatDate(startTimestamp)}, ${formatTime(startTimestamp)} - ${formatTime(endTimestamp)}`,
      totalEvents: 'NA',
      userActiveTime: typeof duration === 'number' ? formatTimeDuration(duration) : duration,
      impacted: impactedFlowIndexDataSet.has(id) ? 'Impacted' : '',
      flowStatus,
      flowErrorStatus,
    };
  });
}

export function getFlowTimelineQueryInterval(start: string, end: string) {
  const startAligned = moment(start).startOf('second');
  const endAligned = moment(end).startOf('second').add(1, 'second');

  return `${startAligned.toISOString()}/${endAligned.toISOString()}`;
}
